/**
 * 기하학적 계산 유틸리티
 * 거리, 각도, 기울기 계산 함수들
 */

/**
 * 두 GPS 좌표사이의 거리를 구하여 cm 단위로 그 결과를 반환한다.
 * @param {Object} p1 - 첫 번째 좌표 {lat, lon}
 * @param {Object} p2 - 두 번째 좌표 {lat, lon}
 * @returns {Number} 거리 (cm 단위)
 */
export function calculateDistance(p1, p2) {
  const lat1 = p1.lat;
  const lon1 = p1.lon;
  const lat2 = p2.lat;
  const lon2 = p2.lon;
  
  if (isNaN(lat1) || isNaN(lon1) || isNaN(lat2) || isNaN(lon2)) {
    console.log(
      `one more of input values is NaN. ${lat1}:${lon1} - ${lat2}:${lon2}`
    );
    return;
  }

  // Convert degrees to radians
  const toRadians = (degrees) => degrees * (Math.PI / 180);

  const R = 6371; // Earth's radius in km
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) ** 2;

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c * 1000 * 100; // Distance in cm, km -> m(1000) -> cm(100)

  return distance;
}

/**
 * 두 GPS 좌표 사이의 기울기를 계산한다.
 * @param {Object} p1 - 첫 번째 좌표 {lat, lon}
 * @param {Object} p2 - 두 번째 좌표 {lat, lon}
 * @returns {Number|String} 기울기 또는 'undefined'
 */
export function calculateSlope(p1, p2) {
  const lat1 = p1.lat;
  const lon1 = p1.lon;
  const lat2 = p2.lat;
  const lon2 = p2.lon;
  // Slope calculation
  const deltaY = lat2 - lat1;
  const deltaX = lon2 - lon1;
  return deltaX !== 0 ? deltaY / deltaX : 'undefined';
}

/**
 * 두 벡터간의 각도를 계산하여 반환한다.
 * @param {Object} v1 - 첫 번째 벡터 {x, y}
 * @param {Object} v2 - 두 번째 벡터 {x, y}
 * @returns {Number} 각도 (도 단위)
 */
export function calculateVectorAngle(v1, v2) {
  if (!v1 || !v2) return 0;
  const dotProduct = v1.x * v2.x + v1.y * v2.y;
  const magnitude1 = Math.sqrt(v1.x ** 2 + v1.y ** 2);
  const magnitude2 = Math.sqrt(v2.x ** 2 + v2.y ** 2);
  if (magnitude1 === 0 || magnitude2 === 0) return 0;
  return Math.acos(dotProduct / (magnitude1 * magnitude2)) * (180 / Math.PI);
}

/**
 * 거리 배열의 요약 통계 계산
 * @param {Array} distances - 거리 배열
 * @returns {Object} {median, mean, max, min}
 */
export function calculateDistanceSummary(distances) {
  if (!distances || distances.length === 0) {
    return { median: 0, mean: 0, max: 0, min: 0 };
  }

  // 중앙값 계산
  const sortedDistances = [...distances].sort((a, b) => a - b);
  const median = sortedDistances.length % 2 === 0
    ? (sortedDistances[sortedDistances.length / 2 - 1] + sortedDistances[sortedDistances.length / 2]) / 2
    : sortedDistances[Math.floor(sortedDistances.length / 2)];

  // 평균값 계산
  const mean = distances.reduce((sum, distance) => sum + distance, 0) / distances.length;

  // 최댓값, 최솟값 계산
  const max = Math.max(...distances);
  const min = Math.min(...distances);

  return { median, mean, max, min };
}

/**
 * 표준편차 계산 및 각 포인트에 distanceFromStdDev 추가
 * @param {Array} points - 포인트 배열 (distance 속성 포함)
 */
export function calculateStandardDeviation(points) {
  // Step 1: Calculate Median
  const distances = [];
  points.forEach((point) => {
    if (point.distance && point.distance > 0) distances.push(point.distance);
  });
  distances.sort((a, b) => a - b);
  const median =
    distances.length % 2 === 0
      ? (distances[distances.length / 2 - 1] +
          distances[distances.length / 2]) /
        2
      : distances[Math.floor(distances.length / 2)];

  // Step 2: Calculate Standard Deviation
  const variance =
    distances.reduce((sum, value) => sum + (value - median) ** 2, 0) /
    distances.length;
  const stdDev = Math.sqrt(variance);

  points.forEach((point) => {
    point.distanceFromStdDev = (point.distance - median) / stdDev;
    point.median = median;
  });
}

/**
 * 좌표 n등분 점 생성
 * @param {Number} lat1 - 시작 위도
 * @param {Number} lon1 - 시작 경도
 * @param {Number} lat2 - 끝 위도
 * @param {Number} lon2 - 끝 경도
 * @param {Number} distance - 전체 거리
 * @param {Number} minDistance - 최소 거리
 * @returns {Array} 생성된 포인트 배열 [{lat, lon}, ...]
 */
export function generatePointsByDivision(lat1, lon1, lat2, lon2, distance, minDistance) {
  // 정수값으로 점 개수 계산
  console.log(
    '테스트imageLocation devArr generatePointsByDivision',
    distance,
    Math.floor(distance / minDistance)
  );
  const pointsNeeded = Math.floor(distance / minDistance);

  if (pointsNeeded < 1) return;

  // 점 위치 배열
  const points = [];

  // 점 생성 (비율 기반 보간)
  for (let i = 1; i <= pointsNeeded; i++) {
    const fraction = i / (pointsNeeded + 1); // n등분 계산
    const interpolatedLat = lat1 + (lat2 - lat1) * fraction;
    const interpolatedLon = lon1 + (lon2 - lon1) * fraction;

    points.push({
      lat: interpolatedLat,
      lon: interpolatedLon
    });
  }

  return points;
}

/**
 * GPS 좌표를 도 단위로 변환
 * @param {Array} coordinate - 좌표 배열 [도, 분, 초]
 * @param {String} reference - 참조 방향 ('N', 'S', 'E', 'W')
 * @returns {Number} 도 단위 좌표
 */
export function convertToDegrees(coordinate, reference) {
  const degrees = coordinate[0] + coordinate[1] / 60 + coordinate[2] / 3600;
  return reference === 'S' || reference === 'W' ? -degrees : degrees;
}

/**
 * 날짜/시간 형식 변환 (정렬용)
 * @param {String|Date} semicolonDate - 날짜 문자열 또는 Date 객체
 * @returns {Number} 타임스탬프 (밀리초)
 */
export function convertDatetimeFormat(semicolonDate) {
  // Date 객체인 경우 직접 변환
  if (semicolonDate instanceof Date) {
    return semicolonDate.getTime();
  }
  
  // 문자열인 경우 기존 로직 사용
  if (typeof semicolonDate === 'string') {
    const tokens = semicolonDate.split(' ');
    if (tokens.length >= 2) {
      const yyyyMMdd = tokens[0].replaceAll(':', '-');
      const hhmmss = tokens[1];
      const dateString = `${yyyyMMdd} ${hhmmss}`;
      return new Date(dateString).getTime();
    }
  }
  
  return NaN;
}

